import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { ArrowRightOutlined } from '@ant-design/icons';
import { IssueEntity, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import {
    selectRequiredFixRequestsApprovals,
    selectRequiresFixRequestsApprovals,
    selectUserById,
} from '@/selectors/selectors';
import { GetFixRequestCurrentApprovals } from '@/helpers';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { antGrey, rwColors } from '@/components/styles/globalStyles';
import { IssueAssigneeAvatar, stateMeta } from './TriageIssueCard';
import { fixRequestStatusMeta, LABEL_STYLE } from './FixRequestModule';
import { IssueSourceIcon } from './triageHelpers';

const STATUS_COL_WIDTH = 185;

const FIX_STATES = [IssueState.FixRequested, IssueState.FixApproved, IssueState.FixMerged, IssueState.FixRejected];

const fixStatusColor = (state: IssueState): string => {
    switch (state) {
        case IssueState.FixApproved:
        case IssueState.FixMerged:
            return rwColors.statusGreen;
        case IssueState.FixRejected:
            return rwColors.statusRed;
        default:
            return rwColors.statusYellow;
    }
};

export const TriageIssueRow: React.FC<{
    issue: IssueEntity;
    orgId: string;
    onOpen?: () => void;
    triagePath?: string;
}> = ({ issue, orgId, onOpen, triagePath }) => {
    const hasFixRequest = !!issue.fixRequest || FIX_STATES.includes(issue.state);
    const requiresFixApprovals = useSelector(selectRequiresFixRequestsApprovals(issue.appId));
    const meta =
        hasFixRequest ?
            fixRequestStatusMeta(issue.state, requiresFixApprovals)
        :   stateMeta(issue.state, requiresFixApprovals);
    const summary = issue.title || issue.description;

    const fixRequest = issue.fixRequest;
    const requester = useSelector((state: RootState) =>
        fixRequest?.createdBy ? selectUserById(state, orgId, issue.appId, fixRequest.createdBy) : undefined,
    );
    const requiredApprovals = useSelector(selectRequiredFixRequestsApprovals(issue.appId)) || 1;
    const currentApprovals = fixRequest?.status ? GetFixRequestCurrentApprovals(fixRequest.status) : 0;
    const fixNotes = (fixRequest?.formFieldsValues ?? []).map((field) => field.value?.trim()).filter(Boolean);
    const fixTooltip =
        fixRequest ?
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!!fixRequest.createdBy && (
                    <span>Requested by {requester ? displayUserNameLong(requester) : fixRequest.createdBy}</span>
                )}
                {fixNotes.map((note, index) => (
                    <span key={index}>{note}</span>
                ))}
                {issue.state === IssueState.FixRequested && requiredApprovals > 1 && (
                    <span>
                        {currentApprovals} of {requiredApprovals} approvals
                    </span>
                )}
            </div>
        :   undefined;

    return (
        <div
            onClick={onOpen}
            style={{
                display: 'flex',
                alignItems: 'center',
                height: 40,
                border: `1px solid ${antGrey.grey5}`,
                borderRadius: 6,
                overflow: 'hidden',
                // A fix request tints the whole row, matching the fix module's darker background in Triage.
                backgroundColor: hasFixRequest ? antGrey.grey4 : antGrey.grey2,
                cursor: onOpen ? 'pointer' : undefined,
            }}
        >
            <div
                style={{
                    width: 3,
                    alignSelf: 'stretch',
                    flexShrink: 0,
                    backgroundColor: hasFixRequest ? fixStatusColor(issue.state) : 'transparent',
                }}
            />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    gap: 8,
                    boxSizing: 'border-box',
                    width: STATUS_COL_WIDTH,
                    flexShrink: 0,
                    padding: '0 12px',
                }}
            >
                <Tooltip title={fixTooltip} placement="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {meta.icon}
                        <Text style={{ ...LABEL_STYLE, whiteSpace: 'nowrap' }}>{meta.label}</Text>
                    </span>
                </Tooltip>
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flex: 1,
                    minWidth: 0,
                    padding: '0 12px',
                }}
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0, flexShrink: 0 }}>
                    <IssueSourceIcon source={issue.sources?.[0]} appId={issue.appId} size={14} />
                </span>

                <Text
                    style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        color: antGrey.grey10,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    title={summary}
                >
                    {summary}
                </Text>

                <div style={{ width: 24, flexShrink: 0, display: 'flex', justifyContent: 'center', marginLeft: 12 }}>
                    <IssueAssigneeAvatar issue={issue} orgId={orgId} size={20} />
                </div>

                {triagePath && (
                    <Tooltip title="Open in Triage" placement="top">
                        <Link
                            to={`${triagePath}/${issue.id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: rwColors.primary, lineHeight: 0 }}
                        >
                            <ArrowRightOutlined />
                        </Link>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};
